import React, { useCallback, useEffect, useState, useMemo } from "react";
import { validator } from "../utils/validator";
import TextField from "../components/common/form/TextField";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentUserData } from "../store/users";
import {
  createRandomGroup,
  getGroupById,
  getGroupByName,
} from "../store/groups";
import SelectField from "../components/common/form/SelectField";
import { getTownById } from "../store/towns";
import CheckBoxField from "../components/common/form/CheckBoxField";
import localStorageService from "../service/http.localStorage";
import { getCafes } from "../store/cafes";
import TimePicker from "../components/common/form/TimePicker";
import { loadIdentify } from "../store/identify";
import { useI18n } from "../i18n/LanguageContext";

const CreateTeamRandom = () => {
  const dispatch = useDispatch();
  const { t } = useI18n();

  const currentUser = useSelector(getCurrentUserData());
  const cafes = useSelector(getCafes());

  const town = useSelector(getTownById(currentUser.town));
  const [data, setData] = useState({
    teamname: "",
    memberOne: currentUser && currentUser._id,
    love: false,
    town: currentUser.town,
    cafe: "",
    time: "",
    identify: "",
    ageRange: "",
  });
  const groupSelector = useMemo(
    () => (currentUser?.teamId ? getGroupById(currentUser.teamId) : () => null),
    [currentUser?.teamId]
  );
  const group = useSelector(groupSelector);

  const teamNameSelector = useMemo(
    () => getGroupByName(data.teamname),
    [data.teamname]
  );
  const team = useSelector(teamNameSelector);

  const [errors, setErrors] = useState({});
  const [block, setBlock] = useState(false);

  const setSelectTown = useCallback((arr) => {
    return arr.map((elem) => ({ value: elem._id, label: elem.name }));
  }, []);

  const validatorConfig = useMemo(
    () => ({
      teamname: {
        isRequired: {
          message: t("team.err.nameRequired"),
        },
        isLetter: {
          message: t("team.err.letters"),
        },
        min: {
          message: t("team.err.length"),
          value: 3,
        },
        checkName: {
          message: t("team.err.exists"),
          value: team,
        },
        max: {
          message: t("team.err.tooLong"),
          value: 32,
        },
      },

      cafe: {
        isRequiredCafe: {
          message: t("random.err.cafe"),
        },
      },
      time: {
        isRequiredTime: {
          message: t("random.err.time"),
        },
      },
      identify: {
        isRequiredIdentify: {
          message: t("random.err.identify"),
        },
        maxIdentify: {
          message: t("random.err.identifyMax"),
          value: 80,
        },
      },
      ageRange: {
        isRequired: { message: t("random.err.age") },
      },
    }),
    [team, t]
  );

  const handleChange = useCallback((event) => {
    setData((prevState) => ({
      ...prevState,
      [event.name]: event.value,
    }));
  }, []);

  const validate = useCallback(() => {
    const errors = validator(data, validatorConfig);
    setErrors(errors);
    return Object.keys(errors).length === 0;
  }, [data, validatorConfig]);

  useEffect(() => {
    validate();
  }, [data]);

  const isValid = Object.keys(errors).length === 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = validate();
    if (!isValid) return;

    const newData = {
      teamname: data.teamname.trim().toLowerCase(),
      status: "build",
      members: [{ member: data.memberOne, accept: true }],
      payment: "unpaid",
      missionId: null,
      playId: null,
      love: data.love,
      town: data.town,
      cafe: data.cafe,
      time: data.time,
      identify: data.identify.trim(),
      ageRange: data.ageRange,
    };

    const createdGroup = await dispatch(
      createRandomGroup({ newData, currentUser })
    );

    if (createdGroup) {
      const local = localStorageService.getGroupId();
      !local && localStorage.setItem("group-local-id", createdGroup._id);
      dispatch(loadIdentify());
    }

    setBlock(true);
  };

  const cafeOptions = useMemo(
    () => setSelectTown(cafes),
    [cafes, setSelectTown]
  );

  // 👇 новые опции возрастов
  const ageOptions = [
    { value: "18-25", label: t("random.age1") },
    { value: "26-35", label: t("random.age2") },
    { value: "36-45", label: t("random.age3") },
    { value: "46-60", label: t("random.age4") },
  ];

  return (
    <>
      {!group ? (
        cafes?.length > 0 ? (
          <div className="relative min-w-0 flex-auto px-1 sm:px-4">
            {/* Информационный блок */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-slate-600 text-base leading-relaxed">
                <span className="font-semibold block mb-2">ℹ️ {t("team.infoLabel")}</span>
                {t("random.info")}
              </p>
            </div>

            {/* Город */}
            <div className="flex flex-row justify-center items-center bg-gray-50 rounded-lg p-3 mb-4">
              <span className="text-gray-600 font-medium mr-2">{t("common.city")}</span>
              <span className="text-gray-800 font-semibold">{town?.name}</span>
            </div>

            {errors.town ? (
              <div className="h-5 text-red-400 text-sm text-center mb-2">
                {errors.town}
              </div>
            ) : (
              <div className="h-5 mb-2"></div>
            )}

            {/* Форма */}
            <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
              {/* Team Name */}
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <TextField
                  label={t("team.name")}
                  name="teamname"
                  placeholder={t("team.namePh")}
                  className="w-full bg-gray-50 text-base py-3 px-4"
                  value={data.teamname}
                  onChange={handleChange}
                  error={errors.teamname}
                />
              </div>

              {/* Checkbox */}
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <CheckBoxField
                  value={data.love}
                  onChange={handleChange}
                  name="love"
                  error={errors.love}
                >
                  <span className="text-base font-medium">{t("random.love")}</span>
                </CheckBoxField>
                <div className="text-sm text-gray-600 mt-2">
                  {t("random.members", {
                    count: !data.love ? t("random.three") : t("random.two"),
                  })}
                </div>
              </div>

              {/* Age Range */}
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <SelectField
                  label={t("random.age")}
                  defaultOption={t("random.agePh")}
                  options={ageOptions}
                  name="ageRange"
                  onChange={handleChange}
                  value={data.ageRange}
                  error={errors.ageRange}
                  className="text-base"
                />
              </div>

              {/* Cafe */}
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <SelectField
                  label={t("random.place")}
                  defaultOption={t("random.placePh")}
                  options={cafeOptions}
                  name="cafe"
                  onChange={handleChange}
                  value={data.cafe}
                  error={errors.cafe}
                  className="text-base"
                />
              </div>

              {/* Time Picker */}
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <TimePicker
                  label={t("random.time")}
                  name="time"
                  onChange={handleChange}
                  value={data.time}
                  error={errors.time}
                />
              </div>

              {/* Identify */}
              <div className="bg-white rounded-lg p-3 shadow-sm">
                <TextField
                  label={t("random.identify")}
                  name="identify"
                  placeholder={t("random.identifyPh")}
                  className="w-full bg-gray-50 text-base py-3 px-4"
                  value={data.identify}
                  onChange={handleChange}
                  error={errors.identify}
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!isValid || block}
                className={`
                  btn-smooth w-full py-4 px-6 rounded-xl font-bold text-lg
                  shadow-lg hover:shadow-xl
                  disabled:opacity-50 disabled:cursor-not-allowed
                  disabled:transform-none disabled:shadow-none
                  ${
                    !isValid || block
                      ? "bg-gray-400 text-gray-200"
                      : "bg-gradient-to-r from-lime-500 to-green-500 text-white hover:from-lime-600 hover:to-green-600"
                  }
                `}
              >
                {block ? t("random.sent") : t("random.create")}
              </button>
            </form>

            {/* Мобильные подсказки */}
            <div className="mt-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 text-center">
                {t("random.hint")}
              </p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center my-10 mx-4 p-6 bg-red-50 border border-red-200 rounded-xl">
            <div className="text-4xl mb-4">😔</div>
            <p className="text-red-600 text-center text-lg font-medium">
              {t("random.noPlaces")}
            </p>
            <p className="text-red-500 text-center mt-2">
              {t("random.tryLater")}
            </p>
          </div>
        )
      ) : (
        <div className="relative px-4 md:px-10 py-8 flex-auto">
          <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
            <p className="text-green-600 text-xl md:text-md">
              {t("random.inTeam", { name: currentUser?.nickname })}
            </p>
          </div>
        </div>
      )}
    </>
  );
};

//   return (
//     <>
//       {!group ? (
//         cafes?.length > 0 ? (
//           <div className="relative md:px-10 flex-auto">
//             <p className="my-4 text-slate-500 text-lg leading-relaxed">
//               Команда до 3-х человек. Галочка Знакомство формирует команду из
//               двух человек противоположного пола. В случае отсутствия
//               формируется команда из трех случайных людей.
//             </p>
//             <div className="flex flex-row justify-center">
//               <div className="mx-5">город</div> <div>{" " + town?.name}</div>
//             </div>
//             {errors.town ? (
//               <div className="h-5 text-red-400 text-sm">{errors.town}</div>
//             ) : (
//               <div className="h-5"> </div>
//             )}
//             <form onSubmit={handleSubmit} className="flex flex-col text-xl">
//               <TextField
//                 label="Team name:"
//                 name="teamname"
//                 placeholder=" Название группы"
//                 className="w-full bg-slate-100"
//                 value={data.teamname}
//                 onChange={handleChange}
//                 error={errors.teamname}
//               />
//               <CheckBoxField
//                 value={data.love}
//                 onChange={handleChange}
//                 name="love"
//                 error={errors.love}
//               >
//                 Знакомство
//               </CheckBoxField>
//               <div>
//                 Команда состоит из {!data.love ? " трех " : " двух "} участников
//               </div>

//               <SelectField
//                 label="Возраст оппонентов"
//                 defaultOption="Choose..."
//                 options={ageOptions}
//                 name="ageRange"
//                 onChange={handleChange}
//                 value={data.ageRange}
//                 error={errors.ageRange}
//               />

//               <SelectField
//                 label="Место встречи"
//                 defaultOption="Choose..."
//                 options={cafeOptions}
//                 name="cafe"
//                 onChange={handleChange}
//                 value={data.cafe}
//                 error={errors.cafe}
//               />

//               <TimePicker
//                 label="Выберите время:"
//                 name="time"
//                 onChange={handleChange}
//                 value={data.time}
//                 error={errors.time}
//               />

//               <TextField
//                 label="Идентификация"
//                 name="identify"
//                 placeholder=" Напишите как узнать друг друга"
//                 className="w-full bg-slate-100"
//                 value={data.identify}
//                 onChange={handleChange}
//                 error={errors.identify}
//               />

//               <input
//                 type="submit"
//                 disabled={!isValid || block === true}
//                 value={block === false ? "создать" : "отправленно"}
//                 className="bg-lime-500 hover:text-white hover:bg-lime-600 font-bold uppercase text-sm px-6 py-2 rounded shadow hover:shadow-lg outline-none focus:outline-none my-5 mt-5 ease-linear transition-all duration-150"
//               />
//             </form>
//           </div>
//         ) : (
//           <div className="flex justify-center my-10 mx-5">
//             Место встреч не указано попытайтесь позже
//           </div>
//         )
//       ) : (
//         <div className="relative px-10 py-14 flex-auto">
//           <p className="flex flex-col font-bold">{`${currentUser.nickname} состоит в команде`}</p>
//         </div>
//       )}
//     </>
//   );
// };

export default CreateTeamRandom;
